import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Terms & Conditions"
      intro="The agreement between you and GetJewelDesigns when you use our platform to commission or deliver jewellery CAD work."
      lastUpdated="20 July 2026"
      path="/terms"
    >
      <p>
        These Terms & Conditions ("<strong>Terms</strong>") govern your access to and use of the
        GetJewelDesigns platform, websites and services (together, the "<strong>Platform</strong>"),
        operated by <strong>Sonam Patel</strong>, a sole proprietor trading as
        "<strong>GetJewelDesigns</strong>" and based in Surat, Gujarat, India ("<strong>GetJewelDesigns</strong>",
        "we", "us"). By creating an account, submitting a request, or otherwise using the Platform, you
        agree to these Terms. If you do not agree, do not use the Platform.
      </p>

      <h2>1. Who we are and what we do</h2>
      <p>
        GetJewelDesigns is a managed design studio for bespoke jewellery CAD (computer-aided design) work.
        Clients submit a design brief; we prepare a fixed-price quotation; and, on approval and
        payment, we produce and deliver the work as digital files, drawing on verified independent design
        specialists engaged by us under the GetJewelDesigns Design Team programme. You contract with
        GetJewelDesigns, which is solely responsible to you for the service, pricing and delivery. We are
        not a manufacturer of physical jewellery, and we are not a marketplace or intermediary between
        you and any third party.
      </p>

      <h2>2. Eligibility and accounts</h2>
      <ul>
        <li>You must be at least 18 years old and able to enter a binding contract.</li>
        <li>You are responsible for the accuracy of the information you provide and for all activity under your account.</li>
        <li>Sign-in uses passwordless email links and/or third-party identity providers. Keep access to your email account secure.</li>
        <li>We may suspend or close accounts that breach these Terms or that we reasonably believe are being used unlawfully.</li>
      </ul>

      <h2>3. Roles</h2>
      <p>The Platform serves three roles, and additional terms apply to each:</p>
      <ul>
        <li><strong>Clients</strong> commission design work and pay for approved quotations.</li>
        <li><strong>Design specialists</strong> ("freelancers") accept and deliver work under the Design Team programme, subject to a separate confidentiality and non-circumvention agreement accepted on application.</li>
        <li><strong>Administrators</strong> operate the Platform, set pricing and manage delivery.</li>
      </ul>

      <h2>4. Quotations, orders and pricing</h2>
      <ul>
        <li>Submitting a brief is free and does not create a contract. A quotation is an offer at a fixed price for a defined scope and estimated turnaround.</li>
        <li>A binding order is formed only when you approve a quotation and payment is received.</li>
        <li>Prices are shown in the currency stated on the quotation and, unless stated otherwise, are inclusive of applicable taxes as displayed at checkout.</li>
        <li>Each quotation includes a defined number of revisions. Work outside the approved scope, new concepts, or additional deliverables may require a new quotation.</li>
      </ul>

      <h2>5. Confidentiality and non-circumvention</h2>
      <p>
        GetJewelDesigns operates a deliberately anonymised model: clients and design specialists interact
        only through the Platform and are not given each other's identity or contact details. You
        agree <strong>not</strong> to attempt to discover, share, or use another party's identity or
        contact information, and not to solicit or transact the same or similar work outside the
        Platform. This restriction is fundamental to our service and survives termination. We may
        moderate or withhold messages and files that attempt to share contact information.
      </p>

      <h2>6. Intellectual property and deliverables</h2>
      <ul>
        <li>Once an order is fully paid and you approve the final work, you receive the delivered production files and the right to use them for the purpose commissioned.</li>
        <li>You are responsible for ensuring that the references and instructions you provide do not infringe anyone else's intellectual property or other rights, and you indemnify us against claims arising from your content.</li>
        <li>You grant GetJewelDesigns a licence to use, store and process your content as needed to provide the service.</li>
        <li>We may display non-identifying examples of work for portfolio and marketing purposes unless you ask us in writing not to.</li>
      </ul>

      <h2>7. Payments</h2>
      <p>
        Payments are processed by our third-party payment processor, <strong>Razorpay</strong>. By
        paying, you authorise the charge and agree to the processor's terms. We do not store your full
        card or bank details. Work begins only after payment is confirmed. Refunds are governed by our{" "}
        <Link to="/refund-policy">Refund Policy</Link>.
      </p>

      <h2>7a. Delivery</h2>
      <p>
        All deliverables are <strong>digital files</strong> delivered electronically through the
        Platform. Nothing is physically shipped, so no shipping charges or delivery addresses apply.
        Estimated turnaround times are shown on each quotation.
      </p>

      <h2>8. Acceptable use</h2>
      <p>You agree not to use the Platform to:</p>
      <ul>
        <li>break the law, infringe rights, or request unlawful or infringing designs;</li>
        <li>circumvent the anonymised model or the payment flow;</li>
        <li>upload malware, or attempt to disrupt, probe or reverse-engineer the Platform;</li>
        <li>misrepresent your identity or your authority to commission work.</li>
      </ul>

      <h2>9. Disclaimers and limitation of liability</h2>
      <p>
        The Platform is provided "as is". To the fullest extent permitted by law, we exclude implied
        warranties and are not liable for indirect or consequential loss. Our total liability arising
        out of or in connection with an order is limited to the amount you paid for that order. Nothing
        in these Terms limits liability that cannot be limited by law.
      </p>

      <h2>10. Suspension and termination</h2>
      <p>
        You may stop using the Platform at any time. We may suspend or terminate access for breach of
        these Terms. Provisions that by their nature should survive termination (including sections 5,
        6, 9 and 11) will do so.
      </p>

      <h2>11. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of <strong>India</strong>, and the courts of{" "}
        <strong>Surat, Gujarat, India</strong> have exclusive jurisdiction, except where mandatory local
        law provides otherwise.
      </p>

      <h2>12. Changes</h2>
      <p>
        We may update these Terms from time to time. Material changes will be notified through the
        Platform or by email. Continued use after changes take effect constitutes acceptance.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms? Contact us at{" "}
        <a href="mailto:getjeweldesigns@gmail.com">getjeweldesigns@gmail.com</a> or via our{" "}
        <Link to="/contact">contact page</Link>.
      </p>
    </LegalLayout>
  );
}
